import { useMemo } from 'react';

import {
  infiniteQueryOptions,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';

import { SECONDS_IN_DAY } from '@cast/constants';
import { SortDirection } from '@cast/design-system';
import { DataTransferCostResponse, NoDataReason } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const CLUSTER_DATA_TRANSFER_COST_QUERY_KEY = 'cluster-data-transfer-cost';

const getPaginatedDataTransferCostQueryKey = ({
  clusterId,
  startTime,
  endTime,
  labels,
  namespaces,
  workloadNames,
  workloadTypes,
  stepSeconds,
  pageLimit,
  sortBy,
  sortDirection,
}: Pick<
  PaginatedDataTransferCostQueryArgs,
  | 'clusterId'
  | 'startTime'
  | 'endTime'
  | 'labels'
  | 'namespaces'
  | 'workloadNames'
  | 'workloadTypes'
  | 'stepSeconds'
  | 'pageLimit'
  | 'sortBy'
  | 'sortDirection'
>) =>
  [
    CLUSTER_DATA_TRANSFER_COST_QUERY_KEY,
    clusterId,
    startTime,
    endTime,
    labels,
    namespaces,
    workloadNames,
    workloadTypes,
    stepSeconds,
    pageLimit,
    sortBy,
    sortDirection,
  ] as const;

type PaginatedDataTransferCostQueryArgs = {
  clusterId: string;
  startTime: string;
  endTime: string;
  labels?: Array<{ label?: string; value?: string }>;
  namespaces?: string[];
  workloadNames?: string[];
  workloadTypes?: string[];
  stepSeconds?: number;
  enabled?: boolean;
  refetchInterval?: (data: unknown) => number | false;
  pageLimit?: number;
  sortBy?: string;
  sortDirection?: SortDirection;
};

type PaginatedDataTransferCostQuery = {
  data: DataTransferCostResponse[];
  count?: number;
  noDataReason?: NoDataReason;
  isLoading: boolean;
  error?: unknown;
  refetch: () => void;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetching: boolean;
};

const getPaginatedDataTransferCostQueryOptions = (
  {
    clusterId,
    startTime,
    endTime,
    stepSeconds = SECONDS_IN_DAY,
    labels,
    namespaces,
    workloadNames,
    workloadTypes,
    enabled = true,
    refetchInterval,
    pageLimit = 50,
    sortBy,
    sortDirection = 'desc',
  }: PaginatedDataTransferCostQueryArgs,
  client: ReturnType<typeof useQueryClient>
) =>
  infiniteQueryOptions<DataTransferCostResponse>({
    queryKey: getPaginatedDataTransferCostQueryKey({
      clusterId,
      startTime,
      endTime,
      labels,
      namespaces,
      workloadNames,
      workloadTypes,
      stepSeconds,
      pageLimit,
      sortBy,
      sortDirection,
    }),
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.costReport.getDataTransferCost(
        {
          clusterId,
          startTime,
          endTime,
          filter: {
            labels,
            namespaces,
            workloadNames,
            workloadTypes,
          },
          stepSeconds,
          pageLimit: String(pageLimit),
          pageCursor: pageParam as string,
          sortField: sortBy,
          sortOrder: sortDirection,
        },
        { timeout: 120_000 }
      );

      return data;
    },
    enabled,
    refetchInterval: ({ queryKey }) => {
      if (refetchInterval) {
        return refetchInterval(client.getQueryData(queryKey));
      }
      return false;
    },
    initialPageParam: '',
    getNextPageParam: ({ nextCursor }) =>
      nextCursor?.length ? nextCursor : undefined,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: true,
  });

export const usePaginatedDataTransferCostQuery = (
  args: PaginatedDataTransferCostQueryArgs
): PaginatedDataTransferCostQuery => {
  const client = useQueryClient();
  const {
    isLoading,
    data,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetching,
  } = useInfiniteQuery(getPaginatedDataTransferCostQueryOptions(args, client));

  const normalizedData: DataTransferCostResponse[] = useMemo(
    () =>
      flatMap(data?.pages, (page) => {
        return {
          ...page,
          items: page.items || [],
        };
      }),
    [data]
  );

  const lastPage = data?.pages.at(-1);

  return {
    data: normalizedData,
    count: parseInt(lastPage?.count || '0', 10),
    noDataReason: lastPage?.noDataReason,
    isLoading,
    isFetching,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
  };
};
